/**
 * Triage Arc
 *
 * Question: "What kind of visit is this?"
 *
 * Entry: proxyQuestion (are you checking in for someone else?)
 * Exit: done (final) — produces TriageOutput
 *
 * Actors: none
 * Signals: GO_BACK (from first interactive state)
 *
 * Navigation map: see README.md
 */

import { sendParent, setup } from "xstate";
import type { TriageInput, TriageOutput } from "../contracts/arc-contracts";
import type { KioskMachineContext, KioskMachineEvent } from "../types";

import { isCheckingInForSomeoneElse } from "../guards/checking-in-for-someone-else.guard";
import { planToUseInsurance } from "../guards/plan-to-use-insurance.guard";
import { isWorkersComp } from "../guards/workers-comp.guard";
import { isWorkplaceInjury } from "../guards/workplace-injury.guard";
// Guards
import { hasCompletedBefore } from "../helper/arc-helpers";

// Local guard: patient is under 18
// biome-ignore lint/suspicious/noExplicitAny: XState guard params are complex
const isUnder18 = ({ context, event }: { context: any; event: any }) => {
  // Check event.data first (guards run before actions)
  if (event.data && typeof event.data.under18 === "boolean") {
    return event.data.under18 === true;
  }
  // Fallback to context (for state restoration)
  return context.under18 === true;
};

// Actions
import { saveFormData } from "../actions/save-form-data.action";

/**
 * Seed arc context from input.
 * Uses KioskMachineContext (wide type) initially — see AGENTS.md Typing Strategy.
 */
function createTriageContext(input: TriageInput): KioskMachineContext {
  // If re-invoked, restore from previous output and flag for routing
  if (input.previousOutput) {
    return {
      orgId: input.orgId,
      orgName: input.orgName,
      practiceId: input.practiceId,
      language: input.language,
      ...input.previousOutput,
      _reInvoked: true,
    };
  }

  return {
    orgId: input.orgId,
    orgName: input.orgName,
    practiceId: input.practiceId,
    language: input.language,
  };
}

/**
 * Extract typed output from arc context at final state.
 */
function extractTriageOutput(context: KioskMachineContext): TriageOutput {
  return {
    checkingInForSomeoneElse:
      (context.checkingInForSomeoneElse as boolean) ?? false,
    under18: (context.under18 as boolean) ?? false,
    isWorkersComp: (context.isWorkersComp as boolean) ?? false,
    isWorkplaceInjury: (context.isWorkplaceInjury as boolean) ?? false,
    planToUseInsurance: (context.planToUseInsurance as boolean) ?? false,
    isTravelInsurance: (context.isTravelInsurance as boolean) ?? false,
  };
}

// ─── Machine Definition ──────────────────────────────────────

export const triageArc = setup({
  types: {
    context: {} as KioskMachineContext,
    events: {} as KioskMachineEvent,
    input: {} as TriageInput,
    output: {} as TriageOutput,
  },
  guards: {
    hasCompletedBefore,
    // biome-ignore lint/suspicious/noExplicitAny: XState guard params are complex
    isCheckingInForSomeoneElse: isCheckingInForSomeoneElse.impl as any,
    // biome-ignore lint/suspicious/noExplicitAny: XState guard params are complex
    isWorkersComp: isWorkersComp.impl as any,
    // biome-ignore lint/suspicious/noExplicitAny: XState guard params are complex
    isWorkplaceInjury: isWorkplaceInjury.impl as any,
    // biome-ignore lint/suspicious/noExplicitAny: XState guard params are complex
    planToUseInsurance: planToUseInsurance.impl as any,
    // biome-ignore lint/suspicious/noExplicitAny: XState guard params are complex
    isUnder18: isUnder18 as any,
  },
  actions: {
    // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
    saveFormData: saveFormData.impl as any,
  },
}).createMachine({
  id: "triage",
  context: ({ input }) => createTriageContext(input),
  output: ({ context }) => extractTriageOutput(context),
  initial: "route",

  states: {
    // ===== ENTRY ROUTING =====
    route: {
      always: [
        // Re-invocation (insurance yes path): resume at travel insurance question
        {
          guard: ({ context }) =>
            Boolean(context._reInvoked) && context.planToUseInsurance === true,
          target: "travelInsuranceQuestion",
        },
        // Re-invocation (workers comp yes path): resume at workplace injury question
        {
          guard: ({ context }) =>
            Boolean(context._reInvoked) && context.isWorkersComp === true,
          target: "workplaceInjuryQuestion",
        },
        // Re-invocation (non-workers-comp path): resume at insurance question
        {
          guard: ({ context }) =>
            Boolean(context._reInvoked) && context.isWorkersComp === false,
          target: "insuranceQuestion",
        },
        // First invocation: start from the beginning
        { target: "proxyQuestion" },
      ],
    },

    // ===== PROXY CHECK =====
    proxyQuestion: {
      meta: {
        componentName: "BinaryQuestionComponent",
        contentKey: "medrite.checkin.checking-in-for-someone-else",
      },
      on: {
        SUBMIT: [
          {
            // Proxy: ask if the person is under 18
            guard: "isCheckingInForSomeoneElse",
            target: "minorQuestion",
            actions: ["saveFormData"],
          },
          {
            // Not proxy: ask about workers comp
            target: "workersCompQuestion",
            actions: ["saveFormData"],
          },
        ],
        // Arc boundary: first interactive state sends GO_BACK to parent
        BACK: {
          actions: sendParent({ type: "GO_BACK" }),
        },
      },
    },

    // ===== MINOR CHECK (only shown when isCheckingInForSomeoneElse = true) =====
    minorQuestion: {
      meta: {
        componentName: "BinaryQuestionComponent",
        contentKey: "medrite.checkin.under-18",
      },
      on: {
        SUBMIT: [
          {
            // Under 18: skip work-related questions, go straight to insurance
            guard: "isUnder18",
            target: "insuranceQuestion",
            actions: ["saveFormData"],
          },
          {
            // Proxy + not under 18: skip workers comp, go to insurance
            guard: "isCheckingInForSomeoneElse",
            target: "insuranceQuestion",
            actions: ["saveFormData"],
          },
          {
            // Self check-in + not under 18: ask about workers comp
            target: "workersCompQuestion",
            actions: ["saveFormData"],
          },
        ],
        BACK: { target: "proxyQuestion" },
      },
    },

    // ===== WORKERS COMP CHECK (only shown on non-proxy track) =====
    workersCompQuestion: {
      meta: {
        componentName: "BinaryQuestionComponent",
        contentKey: "medrite.checkin.workers-comp-question",
      },
      on: {
        SUBMIT: [
          {
            // Workers comp: ask about workplace injury
            guard: "isWorkersComp",
            target: "workplaceInjuryQuestion",
            actions: ["saveFormData"],
          },
          {
            // Not workers comp: ask about insurance
            target: "insuranceQuestion",
            actions: ["saveFormData"],
          },
        ],
        BACK: { target: "proxyQuestion" },
      },
    },

    // ===== WORKPLACE INJURY CHECK (only shown when isWorkersComp = true) =====
    workplaceInjuryQuestion: {
      meta: {
        componentName: "BinaryQuestionComponent",
        contentKey: "medrite.checkin.workplace-injury-question",
      },
      on: {
        SUBMIT: {
          target: "done",
          actions: ["saveFormData"],
        },
        BACK: { target: "workersCompQuestion" },
      },
    },

    // ===== INSURANCE CHECK (shown for proxy track and non-workers-comp self track) =====
    insuranceQuestion: {
      meta: {
        componentName: "BinaryQuestionComponent",
        contentKey: "medrite.checkin.insurance-question",
      },
      on: {
        SUBMIT: [
          {
            // Plans to use insurance: ask about travel insurance
            guard: "planToUseInsurance",
            target: "travelInsuranceQuestion",
            actions: ["saveFormData"],
          },
          {
            // No insurance: exit arc
            target: "done",
            actions: ["saveFormData"],
          },
        ],
        BACK: [
          {
            // Under 18: back to minorQuestion (bypassed workersCompQuestion)
            guard: "isUnder18",
            target: "minorQuestion",
          },
          {
            // Proxy: back to minorQuestion (bypassed workersCompQuestion)
            guard: "isCheckingInForSomeoneElse",
            target: "minorQuestion",
          },
          {
            // Self check-in: back to workersCompQuestion
            target: "workersCompQuestion",
          },
        ],
      },
    },

    // ===== TRAVEL INSURANCE CHECK (only shown when planToUseInsurance = true) =====
    travelInsuranceQuestion: {
      meta: {
        componentName: "BinaryQuestionComponent",
        contentKey: "medrite.checkin.travel-insurance",
      },
      on: {
        SUBMIT: {
          target: "done",
          actions: ["saveFormData"],
        },
        BACK: { target: "insuranceQuestion" },
      },
    },

    // ===== EXIT =====
    done: {
      type: "final" as const,
    },
  },
});
